import { useLocation, useNavigate } from 'react-router-dom';
import {
  Users,
  MapPin,
  Sparkles,
  Inbox,
  Download,
  Lightbulb,
  Flag,
  Bug,
  HeartPulse,
  Activity,
  
  FileText,
  ChevronDown,
  Trash2,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { LucideIcon } from 'lucide-react';
import type { NavBadgeCounts } from '@/config/nav.types';

interface SectionItem {
  label: string;
  href: string;
  icon: LucideIcon;
  exact?: boolean;
  badgeKey?: string;
}

interface SectionGroup {
  /** Route prefix that activates this group */
  matchPrefixes: string[];
  items: SectionItem[];
}

const SECTION_GROUPS: SectionGroup[] = [
  {
    matchPrefixes: ['/app/admin/producers', '/app/admin/producer-interest'],
    items: [
      { label: 'Alla värdar', href: '/app/admin/producers', icon: Users, exact: true },
      { label: 'Success', href: '/app/admin/producers/success', icon: Sparkles },
      { label: 'Ansökningar', href: '/app/admin/producer-interest', icon: Inbox, badgeKey: 'newApplications' },
    ],
  },
  {
    matchPrefixes: ['/app/admin/places', '/app/admin/place-suggestions'],
    items: [
      { label: 'Alla platser', href: '/app/admin/places', icon: MapPin, exact: true },
      { label: 'Platsimport', href: '/app/admin/places/ingest', icon: Download },
      { label: 'Platsförslag', href: '/app/admin/place-suggestions', icon: Lightbulb, badgeKey: 'newPlaceSuggestions' },
      { label: 'Rapporter', href: '/app/admin/places/reports', icon: Flag, badgeKey: 'newPlaceReports' },
    ],
  },
  {
    matchPrefixes: ['/app/admin/system', '/app/admin/system-logs', '/app/admin/email-outbox'],
    items: [
      { label: 'Host Debug', href: '/app/admin/system/host-debug', icon: Bug },
      { label: 'Host Health', href: '/app/admin/system/host-health', icon: HeartPulse },
      { label: 'Systemloggar', href: '/app/admin/system-logs', icon: Activity },
      { label: 'E-postkö', href: '/app/admin/email-outbox', icon: Inbox },
    ],
  },
  {
    matchPrefixes: ['/app/admin/mer', '/app/admin/experiences', '/app/admin/ambassadors', '/app/admin/legal', '/app/admin/cleanup'],
    items: [
      { label: 'Upplevelser', href: '/app/admin/experiences', icon: Sparkles },
      { label: 'Ambassadörer', href: '/app/admin/ambassadors', icon: Users },
      { label: 'Legal', href: '/app/admin/legal/producer-terms', icon: FileText },
      { label: 'Utkast-cleanup', href: '/app/admin/cleanup/drafts', icon: Trash2 },
    ],
  },
];

function findActiveGroup(pathname: string): SectionGroup | null {
  for (const group of SECTION_GROUPS) {
    if (group.matchPrefixes.some((p) => pathname.startsWith(p))) {
      return group;
    }
  }
  return null;
}

interface AdminMobileSectionNavProps {
  badgeCounts?: NavBadgeCounts;
}

/**
 * Mobile-only dropdown section switcher for admin sub-pages.
 * Shows below the topbar when navigating within Värdar, Platser, System or Mer.
 * Mirrors the ShopMobileSectionNav pattern.
 */
export function AdminMobileSectionNav({ badgeCounts = {} }: AdminMobileSectionNavProps) {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  // Only render on admin routes
  if (!pathname.startsWith('/app/admin')) return null;

  const group = findActiveGroup(pathname);
  if (!group) return null;

  // Find active item
  const active = group.items.find((s) => {
    if (s.exact) return pathname === s.href;
    return pathname.startsWith(s.href);
  }) ?? group.items[0];

  const ActiveIcon = active.icon;

  return (
    <div className="sticky top-14 z-20 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 px-4 py-2 md:hidden">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            className="h-9 gap-2 px-3 text-sm font-medium text-foreground w-full justify-between"
          >
            <span className="flex items-center gap-2 min-w-0">
              <ActiveIcon className="h-4 w-4 shrink-0 text-primary" />
              <span className="truncate">{active.label}</span>
            </span>
            <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-[--radix-dropdown-menu-trigger-width]">
          {group.items.map((item) => {
            const Icon = item.icon;
            const isActive = item.exact
              ? pathname === item.href
              : pathname.startsWith(item.href);
            const badge = item.badgeKey ? badgeCounts[item.badgeKey] : undefined;

            return (
              <DropdownMenuItem
                key={item.href}
                onClick={() => navigate(item.href)}
                className={cn(
                  'gap-2 cursor-pointer',
                  isActive && 'bg-accent font-medium'
                )}
              >
                <Icon className="h-4 w-4 shrink-0" />
                <span className="flex-1">{item.label}</span>
                {badge != null && badge > 0 && (
                  <span className="ml-auto flex h-5 min-w-[20px] items-center justify-center rounded-full bg-primary px-1.5 text-[10px] font-bold text-primary-foreground">
                    {badge > 99 ? '99+' : badge}
                  </span>
                )}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}